/**
 * Controlled offline storage for the field app (MOB-011, MOB-013, DOC-001).
 * Case data lives only in this IndexedDB database and is purged on logout,
 * reassignment, completion or remote wipe. Nothing is written to the device's
 * general file system.
 */

const DB_NAME = 'civilos-field';
const VERSION = 1;

let dbp: Promise<IDBDatabase> | null = null;
function db(): Promise<IDBDatabase> {
  if (!dbp) {
    dbp = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, VERSION);
      req.onupgradeneeded = () => {
        const d = req.result;
        d.createObjectStore('papers', { keyPath: 'id' });
        d.createObjectStore('outbox', { keyPath: 'clientId' });
        d.createObjectStore('docs', { keyPath: 'id' });
        d.createObjectStore('meta');
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }
  return dbp;
}

function tx<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T> | void): Promise<T> {
  return db().then(
    (d) =>
      new Promise<T>((resolve, reject) => {
        const t = d.transaction(store, mode);
        const s = t.objectStore(store);
        const r = fn(s);
        t.oncomplete = () => resolve(r ? (r as IDBRequest<T>).result : (undefined as T));
        t.onerror = () => reject(t.error);
      }),
  );
}

export const idb = {
  all: <T>(store: string) => tx<T[]>(store, 'readonly', (s) => s.getAll() as IDBRequest<T[]>),
  get: <T>(store: string, key: IDBValidKey) => tx<T | undefined>(store, 'readonly', (s) => s.get(key) as IDBRequest<T | undefined>),
  put: (store: string, value: unknown, key?: IDBValidKey) => tx(store, 'readwrite', (s) => s.put(value, key)),
  del: (store: string, key: IDBValidKey) => tx(store, 'readwrite', (s) => s.delete(key)),
  clear: (store: string) => tx(store, 'readwrite', (s) => s.clear()),
};

export interface OutboxOp {
  clientId: string;
  type: 'attempt';
  paperId: string;
  baseVersion: number;
  payload: Record<string, unknown>;
  queuedAt: string;
  state: 'pending' | 'held' | 'rejected';
  message?: string;
}

export async function purgeOfflineData(): Promise<void> {
  try {
    for (const s of ['papers', 'outbox', 'docs', 'meta']) await idb.clear(s);
  } catch {
    /* IndexedDB unavailable */
  }
}

/** Remove a single paper and its cached documents from the device. */
export async function purgePaper(id: string, docIds: string[] = []): Promise<void> {
  await idb.del('papers', id);
  for (const d of docIds) await idb.del('docs', d);
}
